import React, { useState, useEffect, useMemo } from 'react'
import { Box, Stack, Loading, Heading, Text, EmptyBox, SimpleBarChart, Card } from '@/components/lib'
import api from '../../../api'
import utils from '../../../utils/utils'

function TrialCommLogs ({ trial }) {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.trials.getCommLogs(trial._id, (data) => {
      setLogs(data.logs)
      setLoading(false)
    })
  }, [trial._id])

  const chartDates = useMemo(() => {
    const today = new Date()
    const oneMonthAgo = today.setDate(today.getDate() - 30)
    const dates = []
    for (let i = 0; i < 31; i++) {
      const chartDate = new Date(oneMonthAgo)
      chartDate.setDate(chartDate.getDate() + i)
      dates.push(chartDate)
    }
    return dates
  }, [])

  const datasets = useMemo(() => {
    if (!logs.length) return []
    const emailData = []
    const smsData = []
    const whatsappData = []
    const pushData = []
    chartDates.forEach((date) => {
      const dateLogs = logs.filter(log => utils.isSameDay(new Date(log.createdAt), date))
      const emailCount = dateLogs.filter(log => log.type === 'email').length
      const smsCount = dateLogs.filter(log => log.type === 'sms').length
      const whatsappCount = dateLogs.filter(log => log.type === 'whatsapp').length
      const pushCount = dateLogs.filter(log => log.type === 'push').length
      emailData.push(emailCount)
      smsData.push(smsCount)
      whatsappData.push(whatsappCount)
      pushData.push(pushCount)
    })
    return [
      {
        label: 'Email',
        data: emailData,
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      },
      {
        label: 'SMS',
        data: smsData,
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1
      },
      {
        label: 'WhatsApp',
        data: whatsappData,
        backgroundColor: 'rgba(255, 206, 86, 0.2)',
        borderColor: 'rgba(255, 206, 86, 1)',
        borderWidth: 1
      },
      {
        label: 'Push',
        data: pushData,
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }
    ]
  }, [logs, chartDates])

  return (
    <Stack gap={5}>
      <Box>
        <Heading as='h3' size='md'>Message log overview</Heading>
        <Text>For audit purposes, Trialflare logs emails, text messages, and push notifications sent on behalf of the trial.</Text>
      </Box>

      {loading && <Box display='flex' justifyContent='center'><Loading /></Box>}
      {!loading && !logs.length &&
        <EmptyBox icon='messages' title='No logs have been recorded in the last 30 days' description='Once your trial begins to send out communications, they will be shown here.' />}

      {!loading && logs.length > 0 &&
        <Stack gap={5}>
          <Stack direction={['column', 'row']} spacing={4}>
            {datasets.map((dataset, i) => (
              <Card key={i} variant='outline' gap={2}>
                <Heading as='h4' size='sm'>{dataset.label}</Heading>
                <Text fontSize='sm' color='fg.muted'>Sent over the last month</Text>
                <Text fontSize='lg'>{dataset.data.reduce((a, b) => a + b, 0)}</Text>
              </Card>
            ))}
          </Stack>

          <Card variant='outline'>
            <SimpleBarChart
              labels={chartDates.map(date => utils.formatDate(date))}
              datasets={datasets}
              options={{ maintainAspectRatio: false }}
            />
          </Card>
        </Stack>}
    </Stack>
  )
}

export default TrialCommLogs
